import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import dayjs from '~/timeInit';

import { Check as CheckIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { Alert, Autocomplete, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { useGetMonthlyVendorSummary } from '~/hooks/apis/vendors/useGetMonthlyVendorSummary';
import { useGetVendorPromotions } from '~/hooks/apis/vendors/useGetVendorPromotions';
import { useVendorSearch } from '~/hooks/apis/vendors/useVendorSearch';
import useAppNavigation from '~/hooks/useAppNavigation';
import useDebounced from '~/hooks/useDebounced';
import useUrlQuery from '~/hooks/useQuery';

import type { Dayjs } from '~/services/TimeService';
import { resolveVendorBySalesforceId } from '~/services/vendorLookup';

import { isValidSalesforceId } from '~/utils/validateSalesforceId';

import { buildMonthlyPerformanceEmail } from './buildEmail';

const MONTH_FORMAT = 'YYYY-MM';
const VENDOR_BY_SFID_QUERY_KEY = 'monthly-lpc-vendor-by-sfid';

function defaultMonth(): Dayjs {
  return dayjs().subtract(1, 'month').startOf('month');
}

function vendorOptionLabel(vendor: App.Vendor): string {
  return `${vendor.name} (${vendor.sfid})`;
}

export default function MonthlyLPCVendorPerformancePage() {
  const { replace } = useAppNavigation();
  const location = useLocation();
  const urlQuery = useUrlQuery();
  const queryClient = useQueryClient();

  const urlVendorSfid = urlQuery.get('vendor') ?? '';
  const urlMonth = urlQuery.get('month') ?? '';

  const month: Dayjs | null = useMemo(() => {
    if (!urlMonth) {
      return defaultMonth();
    }
    const parsed = dayjs(urlMonth, MONTH_FORMAT, true);
    return parsed.isValid() ? parsed : defaultMonth();
  }, [urlMonth]);
  const monthString = month?.isValid() ? month.format(MONTH_FORMAT) : '';

  const vendorByIdQuery = useQuery({
    queryKey: [VENDOR_BY_SFID_QUERY_KEY, urlVendorSfid],
    queryFn: () => resolveVendorBySalesforceId(urlVendorSfid),
    enabled: !!urlVendorSfid && isValidSalesforceId(urlVendorSfid),
    staleTime: 5 * 60 * 1000,
  });
  const vendor: App.Vendor | null = vendorByIdQuery.data ?? null;

  // `null` means the user hasn't typed — fall back to the vendor's label so a URL-loaded
  // vendor shows up in the input. Any string (including '') means the user is editing.
  const [typedInput, setTypedInput] = useState<string | null>(null);
  const vendorInput = typedInput ?? (vendor ? vendorOptionLabel(vendor) : '');
  const debouncedVendorInput = useDebounced(vendorInput, 300);

  const [copied, setCopied] = useState(false);

  const locationRef = useRef(location);
  locationRef.current = location;

  const updateUrl = useCallback(
    (updates: { vendor?: string | null; month?: string | null }) => {
      const current = locationRef.current;
      const params = new URLSearchParams(current.search);
      if ('vendor' in updates) {
        if (updates.vendor) {
          params.set('vendor', updates.vendor);
        } else {
          params.delete('vendor');
        }
      }
      if ('month' in updates) {
        if (updates.month) {
          params.set('month', updates.month);
        } else {
          params.delete('month');
        }
      }
      const newSearch = params.toString();
      replace({
        pathname: current.pathname,
        search: newSearch ? `?${newSearch}` : '',
      });
    },
    [replace],
  );

  const handleVendorChange = useCallback(
    (next: App.Vendor | null) => {
      if (next) {
        // Prime the by-sfid cache so the URL-driven vendorByIdQuery returns instantly
        // instead of round-tripping when we just had the full object in hand.
        queryClient.setQueryData([VENDOR_BY_SFID_QUERY_KEY, next.sfid], next);
      }
      setTypedInput(null);
      updateUrl({ vendor: next?.sfid ?? null });
    },
    [queryClient, updateUrl],
  );

  const handleMonthChange = useCallback(
    (next: Dayjs | null) => {
      updateUrl({ month: next?.isValid() ? next.format(MONTH_FORMAT) : null });
    },
    [updateUrl],
  );

  const vendorSearch = useVendorSearch({
    filter: debouncedVendorInput,
    enabled: debouncedVendorInput.length >= 2 && debouncedVendorInput !== vendor?.name,
  });

  const summaryQuery = useGetMonthlyVendorSummary({
    vendorId: vendor?.sfid ?? null,
    month: monthString,
    type: 'lpc',
  });

  const promotionsWindow = useMemo(() => {
    if (!month?.isValid()) {
      return { from: '', to: '' };
    }
    return {
      from: month.startOf('month').format('YYYY-MM-DD'),
      to: month.endOf('month').format('YYYY-MM-DD'),
    };
  }, [month]);

  const promotionsQuery = useGetVendorPromotions({
    vendorId: vendor?.sfid ?? null,
    from: promotionsWindow.from,
    to: promotionsWindow.to,
    enabled: !!promotionsWindow.from,
  });

  const isLoadingEmail = !!vendor && (summaryQuery.isPending || promotionsQuery.isPending || vendorByIdQuery.isPending);

  const generatedEmail = useMemo(() => {
    if (!summaryQuery.data || !vendor || promotionsQuery.isPending) {
      return '';
    }
    return buildMonthlyPerformanceEmail({
      summary: summaryQuery.data,
      vendorName: vendor.name,
      promotions: promotionsQuery.data?.rows ?? [],
    });
  }, [summaryQuery.data, vendor, promotionsQuery.data, promotionsQuery.isPending]);

  const handleCopy = async () => {
    if (!generatedEmail) {
      return;
    }
    try {
      await navigator.clipboard.writeText(generatedEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available in all contexts
    }
  };

  const vendorOptions = vendorSearch.data?.result ?? [];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        LPC Monthly Vendor Performance — Email Generator
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Generate a monthly performance email for an LPC Tier 1 partner. Pick a vendor and reporting month, then copy the
        generated email into your email client.
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={3} alignItems="flex-start">
        <Autocomplete<App.Vendor>
          sx={{ flex: 1, minWidth: 300 }}
          value={vendor}
          onChange={(_, value) => handleVendorChange(value)}
          inputValue={vendorInput}
          onInputChange={(_, value) => setTypedInput(value)}
          options={vendorOptions}
          getOptionLabel={vendorOptionLabel}
          isOptionEqualToValue={(option, val) => option.sfid === val.sfid}
          loading={vendorSearch.isFetching || vendorByIdQuery.isFetching}
          filterOptions={(options) => options}
          noOptionsText={debouncedVendorInput.length < 2 ? 'Type at least 2 characters' : 'No vendors found'}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vendor"
              placeholder="Search by vendor name, vendor ID, or offer BK"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {vendorSearch.isFetching || vendorByIdQuery.isFetching ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <DatePicker
          label="Reporting month"
          views={['year', 'month']}
          openTo="month"
          format="MMMM YYYY"
          value={month}
          onChange={handleMonthChange}
          maxDate={dayjs().startOf('month')}
          slotProps={{ textField: { sx: { minWidth: 220 } } }}
        />
      </Stack>

      {summaryQuery.isError && (
        <Box mt={3}>
          <Alert severity="error">
            Failed to load summary: {summaryQuery.error instanceof Error ? summaryQuery.error.message : 'Unknown error'}
          </Alert>
        </Box>
      )}

      {promotionsQuery.isError && (
        <Box mt={3}>
          <Alert severity="error">
            Failed to load promotions:{' '}
            {promotionsQuery.error instanceof Error ? promotionsQuery.error.message : 'Unknown error'}
          </Alert>
        </Box>
      )}

      {vendorByIdQuery.isError && (
        <Box mt={3}>
          <Alert severity="error">
            Could not load the vendor referenced in the URL ({urlVendorSfid}). Pick a vendor manually.
          </Alert>
        </Box>
      )}

      {isLoadingEmail && (
        <Box mt={3} display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading…</Typography>
        </Box>
      )}

      {!vendor && !vendorByIdQuery.isFetching && (
        <Box mt={3}>
          <Alert severity="info">Select a vendor to generate the monthly performance email.</Alert>
        </Box>
      )}

      {generatedEmail && (
        <Box mt={3}>
          <Stack direction="row" spacing={2} mb={2} alignItems="center">
            <Button
              variant="contained"
              color="primary"
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              onClick={handleCopy}
            >
              {copied ? 'Copied!' : 'Copy email'}
            </Button>
          </Stack>

          <TextField
            multiline
            minRows={20}
            maxRows={40}
            value={generatedEmail}
            fullWidth
            InputProps={{ readOnly: true }}
          />
        </Box>
      )}
    </Box>
  );
}
